import { useEffect, useState } from 'react';
import { compileStringAsync } from 'sass';
import { Slide, Lightbox } from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import { SwiperClass, Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';

import articleStyles from '../article.module.css';
import { VirtualArticle } from './VirtualArticle';
import { ScrollDirection } from '../Article.context';
import { DeviceType } from '../CSS';

import { VirtualStyleBuilder } from './VirtualStyleBuilder';
import { LightboxArgs } from './VirtualArticleContext';

export function VirtualArticles(props: Readonly<VirtualArticlesProps>) {
	const {
		index,
		lang,
		setSwiper,
		onTransitionEnd,
		articles,
		device,
		styles,
		setScrollDirection,
		setNavbarVisibility,
		navigateToArticle,
	} = props;
	const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
	const [lighboxSlides, setLighboxSlides] = useState<Array<Slide>>([]);

	const { css, classNameMap } = useStyle(articles, styles);

	const onLighboxOpen = (args: LightboxArgs) => {
		console.log(`ARGS: `, args);
		const { src, caption } = args;
		setLighboxSlides([
			{
				src,
				description: caption ? (
					<p
						className="lightbox-caption"
						dangerouslySetInnerHTML={{ __html: caption }}
					/>
				) : (
					undefined
				),
			},
		]);
		setIsLightboxOpen(true);
	};

	return css ? (
		<>
			<Swiper
				modules={[Virtual]}
				updateOnWindowResize={true}
				initialSlide={index}
				slidesPerView={1}
				slidesPerGroup={1}
				centeredSlides={true}
				spaceBetween={0}
				direction="horizontal"
				maxBackfaceHiddenSlides={20}
				preventClicks={true}
				virtual
				scrollbar={{
					draggable: true,
				}}
				onSwiper={setSwiper}
				onTransitionEnd={onTransitionEnd}
			>
				{articles.map(
					mapSlide({
						index,
						lang,
						device,
						classNameMap,
						setScrollDirection,
						setNavbarVisibility,
						navigateToArticle,
						onLighboxOpen,
					}),
				)}
			</Swiper>
			<Lightbox
				carousel={{ finite: lighboxSlides.length <= 1 }}
				plugins={[Captions, Zoom, Fullscreen]}
				open={isLightboxOpen}
				close={() => setIsLightboxOpen(false)}
				slides={lighboxSlides}
				render={{
					buttonPrev:
						lighboxSlides.length <= 1 ? () => null : undefined,
					buttonNext:
						lighboxSlides.length <= 1 ? () => null : undefined,
				}}
			/>
			<style>{css}</style>
		</>
	) : null;
}

interface VirtualArticlesProps {
	lang: string;
	fontSize: number;
	virtual?: boolean;
	styles: Map<string, any>;
	articles: Array<any>;
	isTouchDevice: boolean | undefined;
	index: number;
	device: DeviceType;
	onTransitionEnd: (s: SwiperClass) => void;
	swiper: SwiperClass | null;
	setSwiper: (s: SwiperClass) => void;
	setScrollDirection?: (direction: ScrollDirection) => void;
	setNavbarVisibility: any;
	navigateToArticle?: (articleId: number) => void;
}

/*
	React hook that creates a css style for all the articles
*/
export function useStyle(
	articles: Array<any>,
	styles: Map<string, any>,
): UseStlyleResponse {
	const [css, setCss] = useState('');
	const [classNameMap, setClassNameMap] = useState<Map<string, string>>(
		new Map(),
	);
	useEffect(() => {
		if (styles.size === 0) {
			return;
		}
		const builder = new VirtualStyleBuilder(articles, styles);
		builder
			.build()
			.then(({ css, classNameMap }) => {
				setClassNameMap(classNameMap);
				return compileStringAsync(css);
			})
			.then(result => {
				console.log(`Sass compilation success`);
				setCss(result.css);
			})
			.catch(e => {
				console.log(`Error processing styles`, e);
				setCss('');
			});
	}, [styles]);
	return {
		css,
		classNameMap,
	};
}

interface UseStlyleResponse {
	css: string;
	classNameMap: Map<string, string>;
}

function mapSlide({
	index,
	lang,
	device,
	classNameMap,
	setScrollDirection,
	setNavbarVisibility,
	navigateToArticle,
	onLighboxOpen,
}: MapSlideArgs) {
	return (article: any, i: number) => {
		const classNames = [
			articleStyles['articles-virtual'],
			'articles-virtual',
		];
		return (
			<span key={i}>
				<SwiperSlide virtualIndex={i} className={classNames.join(' ')}>
					<VirtualArticle
						article={article}
						index={i}
						lang={lang}
						onLighboxOpen={onLighboxOpen}
						isSelected={i === index}
						setNavbarVisibility={setNavbarVisibility}
						device={device}
						navigateToArticle={navigateToArticle}
						setScrollDirection={setScrollDirection}
						className={classNameMap.get(`${article.id}`)}
					/>
				</SwiperSlide>
			</span>
		);
	};
}

interface MapSlideArgs {
	index: number;
	lang: string;
	isTouchDevice?: boolean;
	device: DeviceType;
	classNameMap: Map<string, string>;
	setScrollDirection?: (direction: ScrollDirection) => void;
	setNavbarVisibility: any;
	navigateToArticle?: (articleId: number) => void;
	onLighboxOpen: (props: LightboxArgs) => void;
}
